import { useState, type FormEvent } from 'react';
import { Link, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { kanal } from '@beholder/shared';
import { MINDMAP_LISTEN_KANAL, type MindmapKarte, type MindmapStand } from '@beholder/shared/module/mindmap';
import { api, modulPfad } from '../../core/api';
import { useLive } from '../../core/live';
import type { ModulSeiteProps, WebModul } from '../../core/modul';
import { Symbol } from '../../core/symbole';
import { Dialog, Eingabe, Feld, Karte as KarteUi, Knopf, Laden, Leer, Meldung, datumZeit, useAktion, useDaten } from '../../core/ui';
import { Editor } from './Editor';
import { ImportDialog } from './ImportDialog';
import './stil.css';

function useKarten(projektId: string) {
  const d = useDaten(() => api.get<MindmapKarte[]>(modulPfad(projektId, 'mindmap', 'karten')), [projektId]);
  useLive(kanal(projektId, 'mindmap', MINDMAP_LISTEN_KANAL), () => void d.neuLaden());
  return d;
}

function KartenListe({ projekt, darfSchreiben }: ModulSeiteProps) {
  const { daten, fehler, laedt, neuLaden } = useKarten(projekt.id);
  const [neu, setNeu] = useState(false);
  const [einlesen, setEinlesen] = useState(false);
  const [titel, setTitel] = useState('');
  const a = useAktion();
  const nav = useNavigate();
  const mitglieder = useDaten(() => api.get<{ anzeigename: string }[]>(`/api/projekte/${projekt.id}/mitglieder`), [projekt.id]);

  async function anlegen(e: FormEvent) {
    e.preventDefault();
    const s = await a.ausfuehren(() => api.post<MindmapStand>(modulPfad(projekt.id, 'mindmap', 'karten'), { titel }));
    if (s) {
      setNeu(false);
      setTitel('');
      nav(s.karte.id);
    }
  }

  async function umbenennen(k: MindmapKarte) {
    const t = prompt('Neuer Name der Mindmap:', k.titel);
    if (!t?.trim() || t.trim() === k.titel) return;
    await a.ausfuehren(() => api.patch(modulPfad(projekt.id, 'mindmap', `karten/${k.id}`), { titel: t.trim() }));
    await neuLaden();
  }

  async function loeschen(k: MindmapKarte) {
    if (!confirm(`Die Mindmap „${k.titel}“ mit ${k.knotenAnzahl} Punkten und allen Anlagen in den Papierkorb legen? Dort lässt sie sich 180 Tage lang zurückholen.`)) return;
    await a.ausfuehren(() => api.delete(modulPfad(projekt.id, 'mindmap', `karten/${k.id}`)));
    await neuLaden();
  }

  return (
    <div className="seite">
      <header className="seiten-kopf">
        <h1>Mindmaps</h1>
        {darfSchreiben && (
          <div className="reihe">
            <Knopf onClick={() => setEinlesen(true)}>{Symbol.hochladen} Aus Datei einlesen</Knopf>
            <Knopf art="haupt" onClick={() => setNeu(true)}>
              {Symbol.plus} Neue Mindmap
            </Knopf>
          </div>
        )}
      </header>
      <Meldung>{fehler || a.fehler}</Meldung>
      {laedt && !daten && <Laden />}
      {daten?.length === 0 && (
        <Leer titel="Noch keine Mindmap">
          {darfSchreiben ? 'Lege eine neue Mindmap an oder lies eine Tabelle (CSV/Excel) ein.' : 'Sobald jemand eine Mindmap anlegt, erscheint sie hier.'}
        </Leer>
      )}
      <div className="raster">
        {daten?.map((k) => (
          <div key={k.id} className="projekt-kachel mm-kachel">
            <Link to={k.id} className="mm-kachel-link">
              <strong>{k.titel}</strong>
              <span className="leise klein">
                {k.knotenAnzahl} {k.knotenAnzahl === 1 ? 'Punkt' : 'Punkte'} · geändert {datumZeit(k.geaendertAm)}
              </span>
              {k.erstelltVonName && <span className="leise klein">angelegt von {k.erstelltVonName}</span>}
            </Link>
            <div className="reihe mm-kachel-aktionen">
              <Link to={`${k.id}/tabelle`} className="knopf knopf-klein knopf-leise">
                Tabelle
              </Link>
              {darfSchreiben && (
                <>
                  <Knopf klein art="leise" onClick={() => void umbenennen(k)}>
                    Umbenennen
                  </Knopf>
                  <Knopf klein art="leise" onClick={() => void loeschen(k)} aria-label={`${k.titel} löschen`}>
                    {Symbol.muell}
                  </Knopf>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
      <Dialog
        titel="Neue Mindmap"
        offen={neu}
        schliessen={() => setNeu(false)}
        fuss={
          <>
            <Knopf art="haupt" onClick={(e) => void anlegen(e as unknown as FormEvent)} disabled={a.laeuft || !titel.trim()}>
              Anlegen
            </Knopf>
            <Knopf onClick={() => setNeu(false)}>Abbrechen</Knopf>
          </>
        }
      >
        <form onSubmit={anlegen}>
          <Feld titel="Thema in der Mitte" hinweis="Das ist der mittlere Punkt der Mindmap – lässt sich später ändern.">
            <Eingabe value={titel} onChange={(e) => setTitel(e.target.value)} maxLength={500} placeholder="z. B. Sommerfest 2027" />
          </Feld>
        </form>
      </Dialog>
      <ImportDialog
        offen={einlesen}
        schliessen={() => setEinlesen(false)}
        projektId={projekt.id}
        personen={(mitglieder.daten ?? []).map((m) => m.anzeigename)}
        fertig={(id) => {
          setEinlesen(false);
          nav(id);
        }}
      />
    </div>
  );
}

/** Neuer Zustand je Karte (eingeklappte Äste, Auswahl …). */
function EditorJeKarte(props: ModulSeiteProps) {
  const { karteId } = useParams();
  return <Editor key={karteId} {...props} />;
}

function Seite(props: ModulSeiteProps) {
  return (
    <Routes>
      <Route index element={<KartenListe {...props} />} />
      <Route path=":karteId/*" element={<EditorJeKarte {...props} />} />
    </Routes>
  );
}

function Karte({ projekt }: ModulSeiteProps) {
  const { daten } = useKarten(projekt.id);
  const punkte = (daten ?? []).reduce((s, k) => s + k.knotenAnzahl, 0);
  return (
    <KarteUi
      titel={
        <Link to={`/p/${projekt.id}/mindmap`} className="mm-karte-titel">
          {Symbol.mindmap} Mindmaps
        </Link>
      }
    >
      {!daten ? (
        <Laden />
      ) : daten.length === 0 ? (
        <p className="leise">Noch keine Mindmap.</p>
      ) : (
        <>
          <p className="leise klein">
            {daten.length} {daten.length === 1 ? 'Mindmap' : 'Mindmaps'} mit zusammen {punkte} Punkten
          </p>
          <ul className="mm-karte-liste">
            {daten.slice(0, 5).map((k) => (
              <li key={k.id}>
                <Link to={`/p/${projekt.id}/mindmap/${k.id}`}>{k.titel}</Link>
                <span className="leise klein">{k.knotenAnzahl}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </KarteUi>
  );
}

const mindmap: WebModul = { id: 'mindmap', name: 'Mindmap', reihenfolge: 10, symbol: Symbol.mindmap, Seite, Karte };
export default mindmap;
